'use client';

import { motion } from 'framer-motion';
import { Sparkles, Leaf, Droplet, Sun, ChevronRight } from 'lucide-react';
import { useDesign } from '@/context/DesignContext';

interface CategoriesSectionProps {
  title?: string;
  subtitle?: string;
  categories?: Array<{
    name: string;
    count: string;
    icon: string;
    color: string;
  }>;
  design?: any;
}

const iconMap: Record<string, React.ElementType> = {
  Sparkles,
  Leaf,
  Droplet,
  Sun,
};

const colorMap: Record<string, string> = {
  'rose-pink': 'from-rose-400 to-pink-600',
  'emerald-green': 'from-emerald-400 to-green-600',
  'cyan-blue': 'from-cyan-400 to-blue-600',
  'amber-orange': 'from-amber-400 to-orange-500',
};

export function CategoriesSection({
  title = 'Nos Catégories',
  subtitle = 'Explorez notre gamme complète de produits pour chaque besoin',
  categories = [
    { name: 'Soins Visage', count: '124 produits', icon: 'Sparkles', color: 'rose-pink' },
    { name: 'Compléments', count: '89 produits', icon: 'Leaf', color: 'emerald-green' },
    { name: 'Hygiène', count: '67 produits', icon: 'Droplet', color: 'cyan-blue' },
    { name: 'Solaire', count: '34 produits', icon: 'Sun', color: 'amber-orange' }
  ],
  design: propDesign
}: CategoriesSectionProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" as const } }
  };

  const bgSection = design?.componentDesignMap?.CategoriesSection?.sectionBackground || 'transparent';
  const titleColor = design?.componentDesignMap?.CategoriesSection?.titleColor || design?.colors?.text || '#0F172A';
  const subtitleColor = design?.componentDesignMap?.CategoriesSection?.subtitleColor || design?.colors?.textLight || '#64748B';
  const cardBg = design?.componentDesignMap?.CategoriesSection?.cardBackground || '#FFFFFF';
  const cardBorder = design?.componentDesignMap?.CategoriesSection?.cardBorderColor || design?.colors?.border || '#E2E8F0';
  const cardShadow = design?.componentDesignMap?.CategoriesSection?.cardShadow || design?.effects?.boxShadow || '0 10px 25px -5px rgba(0,0,0,0.05)';
  const cardHoverShadow = design?.componentDesignMap?.CategoriesSection?.cardHoverShadow || design?.effects?.hoverShadow || '0 20px 25px -5px rgba(0,0,0,0.1)';
  const iconGradientStart = design?.componentDesignMap?.CategoriesSection?.iconGradientStart || 'from-rose-400';
  const iconGradientEnd = design?.componentDesignMap?.CategoriesSection?.iconGradientEnd || 'to-pink-600';
  const iconTextColor = design?.componentDesignMap?.CategoriesSection?.iconTextColor || '#FFFFFF';
  const categoryNameColor = design?.componentDesignMap?.CategoriesSection?.categoryNameColor || design?.colors?.text || '#0F172A';
  const countColor = design?.componentDesignMap?.CategoriesSection?.countColor || design?.colors?.textLight || '#64748B';
  const linkColor = design?.componentDesignMap?.CategoriesSection?.linkColor || design?.colors?.text || '#0F172A';
  const linkHoverColor = design?.componentDesignMap?.CategoriesSection?.linkHoverColor || design?.colors?.text || '#0F172A';

  return (
    <section className="py-20 lg:py-32" style={{ background: bgSection }}>
      <div className="container mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: titleColor }}>{title}</h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: subtitleColor }}>{subtitle}</p>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {categories.map((category, index) => {
            const IconComponent = iconMap[category.icon] || Sparkles;
            const dynamicColorClass = colorMap[category.color] || `${iconGradientStart} ${iconGradientEnd}`;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative rounded-2xl p-6 shadow-lg transition-all cursor-pointer border"
                style={{ background: cardBg, borderColor: cardBorder, boxShadow: cardShadow }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = cardHoverShadow}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = cardShadow}
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${dynamicColorClass} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <IconComponent className="w-8 h-8" style={{ color: iconTextColor }} />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: categoryNameColor }}>{category.name}</h3>
                <p className="text-sm" style={{ color: countColor }}>{category.count}</p>
                <div className="mt-4 flex items-center text-sm font-medium" style={{ color: linkColor }}>
                  <span>Voir tout</span>
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" style={{ color: linkColor }} />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}